import React, { useMemo } from 'react';
import MdiIcon from '@mdi/react';
import { mdiAccountMultipleOutline, mdiApplicationOutline, mdiChevronDown, mdiChevronRight, mdiClose } from '@mdi/js';
import { Chip, IconButton, TextField, Tooltip } from '@lumx/react';
import { mdiMagnify } from '@mdi/js';
import { MobileConfig, NodeType, OUNode, Site, TreeNode } from '../types';
import { buildTree, collectAllNodeIds } from '../utils/treeUtils';
import { Icon } from './shared/icons';
import MobileConfigPicker from './shared/MobileConfigPicker';
import TreeRenderer from './tree/TreeRenderer';
import AddUnitForm from './tree/AddUnitForm';

interface Props {
  nodes: OUNode[];
  sites: Site[];
  mobileConfigs: MobileConfig[];
  nodesWithGlobal: OUNode[];
  globalMobileConfigId: string | null;
  expandedIds: Set<string>;
  searchQuery: string;
  addingChildOf: string | null | 'global' | 'none';
  dragOverId: string | null;
  draggingId: string | null;
  dragInsert: { targetId: string; position: 'before' | 'after' } | null;
  isGlobalExpanded: boolean;
  onSearchChange: (q: string) => void;
  onToggleExpand: (id: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onSetGlobalExpanded: (v: boolean) => void;
  onAddChild: (parentId: string | null | 'global') => void;
  onAddChildSave: (name: string, type: NodeType, parentId: string | null) => void;
  onAddChildCancel: () => void;
  onRename: (id: string, name: string) => void;
  onConvertType: (id: string) => void;
  onDeleteRequest: (id: string) => void;
  onMobileConfigChange: (nodeId: string, configId: string | null, conflict: { configId: string; fromNodeName: string } | null) => void;
  onGlobalMobileConfigChange: (configId: string | null, conflict: { configId: string; fromNodeName: string } | null) => void;
  sitesExpandedIds: Set<string>;
  onSitesToggle: (id: string) => void;
  onViewUsers: (id: string) => void;
  onViewSiteGroups: (siteId: string, siteName: string) => void;
  onUnassign?: (siteId: string) => void;
  onDragStart: (e: React.DragEvent, kind: 'ou', id: string) => void;
  onOUDragOver: (e: React.DragEvent, id: string) => void;
  onOUDrop: (e: React.DragEvent, id: string) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onGlobalDragOver: (e: React.DragEvent) => void;
  onGlobalDrop: (e: React.DragEvent) => void;
  onSiteDropOnOU: (e: React.DragEvent, nodeId: string) => void;
  readOnlyStructure?: boolean;
}

const OrgStructurePane: React.FC<Props> = (props) => {
  const {
    nodes, sites, mobileConfigs, nodesWithGlobal, globalMobileConfigId,
    expandedIds, searchQuery, addingChildOf, dragOverId, draggingId, dragInsert,
    isGlobalExpanded,
  } = props;

  const tree = useMemo(() => buildTree(nodes, []), [nodes]);
  const globalSiteCount = useMemo(() => sites.filter(s => s.assignedNodeId === '__global__').length, [sites]);
  const globalSitesExpanded = props.sitesExpandedIds.has('__global__');

  const filteredTree = useMemo(() => {
    if (!searchQuery) return tree;
    const q = searchQuery.toLowerCase();
    function filterNode(n: TreeNode): TreeNode | null {
      const matchSelf = n.name.toLowerCase().includes(q);
      const filteredChildren = n.children.map(filterNode).filter(Boolean) as TreeNode[];
      if (matchSelf || filteredChildren.length > 0) return { ...n, children: filteredChildren, sites: [] };
      return null;
    }
    return tree.map(filterNode).filter(Boolean) as TreeNode[];
  }, [tree, searchQuery]);

  // Combined drop handler: accepts both OU drags (reparent/reorder) and site drags (assign)
  const makeRowDragOver = (id: string) => (e: React.DragEvent) => {
    // Let parent decide based on payload kind
    props.onOUDragOver(e, id);
  };

  const makeRowDrop = (id: string) => (e: React.DragEvent) => {
    e.preventDefault();
    // Check if it's a site drag via dataTransfer (payload managed in parent)
    props.onSiteDropOnOU(e, id);
  };

  return (
    <div className="pane pane--org">
      {/* Pane header */}
      <div className="pane-header">
        <div className="pane-title">
          <span className="pane-title-text">Org structure</span>
          <span className="pane-count">{nodes.length} units</span>
        </div>
        <div className="pane-header-actions">
          {!props.readOnlyStructure && (
            <button className="pane-action-btn" onClick={() => props.onAddChild('global')} title="Add unit">
              {Icon.plus} Add
            </button>
          )}
          <button className="pane-action-btn icon-only" onClick={props.onExpandAll} title="Expand all">
            {Icon.expandAll}
          </button>
          <button className="pane-action-btn icon-only" onClick={props.onCollapseAll} title="Collapse all">
            {Icon.collapseAll}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="pane-search">
        <TextField
          placeholder="Search org units &amp; locations…"
          value={searchQuery}
          onChange={props.onSearchChange}
          icon={mdiMagnify}
          clearButtonProps={{ label: 'Clear' }}
        />
      </div>

      {/* Tree */}
      <div className="pane-tree">
        {/* Column headers */}
        <div className="org-tree-thead">
          <div className="tree-th" />
          <div className="tree-th">Name</div>
          <div className="tree-th">
            Mobile config
            <Tooltip label="A mobile configuration can only be mapped to one Org unit or Location at a time.">
              <span className="tooltip-icon" tabIndex={0}>{Icon.info}</span>
            </Tooltip>
          </div>
          <div className="tree-th" />
        </div>

        {/* Global / Platform root row */}
        <div
          className={`global-row ${dragOverId === '__global__' ? 'drag-over' : ''}`}
          onDragOver={props.onGlobalDragOver}
          onDragLeave={props.onDragLeave}
          onDrop={props.onGlobalDrop}
        >
          <div className="row-controls">
            <button
              className={`expand-btn ${isGlobalExpanded ? 'expanded' : ''}`}
              onClick={() => props.onSetGlobalExpanded(!isGlobalExpanded)}
            >
              {Icon.chevronRight}
            </button>
          </div>
          <div className="global-row-name">
            <span className="global-icon">{Icon.globe}</span>
            Global / Platform
            {globalSiteCount > 0 && (
              <Chip
                size="s"
                isClickable
                before={<span style={{ display: 'flex', alignItems: 'center' }}><MdiIcon path={mdiApplicationOutline} size={0.55} /></span>}
                after={<span style={{ display: 'flex', alignItems: 'center' }}><MdiIcon path={globalSitesExpanded ? mdiChevronDown : mdiChevronRight} size={0.65} /></span>}
                onClick={e => { (e as React.MouseEvent).stopPropagation(); props.onSitesToggle('__global__'); }}
              >
                {globalSiteCount} site{globalSiteCount > 1 ? 's' : ''}
              </Chip>
            )}
            {!props.readOnlyStructure && (
              <button
                className="add-unit-btn"
                style={{ opacity: 1, marginLeft: 8 }}
                onClick={() => props.onAddChild('global')}
              >
                {Icon.plus} Add unit
              </button>
            )}
          </div>
          <div className="mobile-config-cell">
            <MobileConfigPicker
              value={globalMobileConfigId}
              configs={mobileConfigs}
              nodes={nodesWithGlobal}
              nodeId="__global__"
              onChange={props.onGlobalMobileConfigChange}
            />
          </div>
          <div className="row-actions-cell">
            <IconButton
              label="View groups"
              icon={mdiAccountMultipleOutline}
              emphasis="low"
              size="s"
              onClick={e => { (e as React.MouseEvent).stopPropagation(); props.onViewUsers('__global__'); }}
            />
          </div>
        </div>

        {globalSitesExpanded && globalSiteCount > 0 && (
          <div className="site-rows-box">
            {sites.filter(s => s.assignedNodeId === '__global__').map(site => (
              <div key={site.id} className="site-sub-row" style={{ paddingLeft: 60 }}>
                <span className="site-sub-row__icon">
                  <MdiIcon path={mdiApplicationOutline} size={0.65} color="var(--color-dark-l1)" />
                </span>
                <span className="site-sub-row__name">{site.name}</span>
                <IconButton
                  label="View site groups"
                  icon={mdiAccountMultipleOutline}
                  emphasis="low"
                  size="s"
                  onClick={e => { (e as React.MouseEvent).stopPropagation(); props.onViewSiteGroups(site.id, site.name); }}
                />
                {props.onUnassign && (
                  <button
                    className="site-sub-row__remove"
                    onClick={() => props.onUnassign!(site.id)}
                    title="Remove"
                  >
                    <MdiIcon path={mdiClose} size={0.6} />
                    <span>Remove</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {isGlobalExpanded && (
          <>
            {!props.readOnlyStructure && addingChildOf === 'global' && (
              <div style={{ borderBottom: '1px solid var(--color-border)' }}>
                <AddUnitForm
                  parentId={null}
                  nodes={nodes}
                  onSave={props.onAddChildSave}
                  onCancel={props.onAddChildCancel}
                />
              </div>
            )}

            {filteredTree.length > 0 ? (
              <div className="children-box depth-0">
                <TreeRenderer
                  nodes={filteredTree}
                  depth={1}
                  expandedIds={expandedIds}
                  addingChildOf={
                    addingChildOf === 'global' || addingChildOf === 'none' || addingChildOf === null
                      ? null
                      : addingChildOf
                  }
                  mobileConfigs={mobileConfigs}
                  allNodes={nodesWithGlobal}
                  allSites={sites}
                  onToggle={props.onToggleExpand}
                  onRename={props.onRename}
                  onConvertType={props.onConvertType}
                  onAddChild={id => props.onAddChild(id)}
                  onDeleteRequest={props.onDeleteRequest}
                  onAddChildSave={props.onAddChildSave}
                  onAddChildCancel={props.onAddChildCancel}
                  onMobileConfigChange={props.onMobileConfigChange}
                  sitesExpandedIds={props.sitesExpandedIds}
                  onSitesToggle={props.onSitesToggle}
                  onViewUsers={props.onViewUsers}
                  onViewSiteGroups={props.onViewSiteGroups}
                  onDragStart={props.onDragStart}
                  onDragOver={props.onOUDragOver}
                  onDrop={props.onOUDrop}
                  onDragLeave={props.onDragLeave}
                  dragOverId={dragOverId}
                  draggingId={draggingId}
                  dragInsert={dragInsert}
                  readOnlyStructure={props.readOnlyStructure}
                  onUnassign={props.onUnassign}
                />
              </div>
            ) : (
              <div className="empty-tree">
                {searchQuery
                  ? `No results for "${searchQuery}"`
                  : 'No units added yet. Use "Add" to create your first Org unit or Location.'}
              </div>
            )}
          </>
        )}
      </div>

      {/* Drop hint when dragging a site */}
      <div className="org-pane-drop-hint">
        Drag any site from the right onto a node to assign it.
      </div>
    </div>
  );
};

export default OrgStructurePane;
